import { useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Slot } from 'radix-ui';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useExperimentButton, useExplicitCtaExperiment } from '@/hooks/analytics/useExplicitCtaExperiment';
import { AccountModalNames, ModalConversionPoints, useAccountModals } from '@/components/Modals/Account';
import { useStage } from '@/components/DialogStage';
import { pauseDownloadWarningAssetAtom } from '@/components/DialogStage/atom';
import { useOpenDownloadAuth } from '@/components/DialogStage/useOpenDownloadAuth';
import { isFirstDownloadTerminalDecision, useOpenFirstDownloadUsage, useShouldOfferFirstDownloadUsage } from '@/components/DialogStage/useFirstDownloadUsage';
import SteppedDialog from '@/components/SteppedDialog';
import { MenuContext } from '@/components/ui/menu/menu-context';
import SteppedDrawer from '@/components/SteppedDrawer';
import Tooltip from '@/components/Tooltip';
import { useAssetUpsellTrigger, useDownloadGateway, useIsUpsellModalOpen } from '@/features/Downloads/Gateway';
import { createAssetInfo } from '@/features/Downloads/create-asset-info-for-upsell';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useSubscriptionType } from '@/hooks/auth';
import { useOverduePayment } from '@/hooks/billing/useOverduePayment';
import { useDownloads } from '@/hooks/markers/useDownloads';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { cn } from '@/lib/utils';
import { logEvent } from '@/pages/api/v1/utils';
import { ASSET_TYPE } from '@/types/asset';
import { Button } from '@music-vine/cadence/ui';
import { PaymentIssueDownloadDialog } from './components/PaymentIssueDownloadDialog';
import { useFreePremiumDownloadArm } from './hooks/useFreePremiumDownloadArm';
import { DownloadedRing } from './icons/downloaded-ring';
import { SteppedDialogContentContainer } from './components/SteppedDialogContentContainer';
import { ConfirmDownloadModalContext } from './context';
import { ConfirmDownloadScreensSkeleton } from './screens-skeleton';

// The download button ships on every track/SFX tile, so it has to stay in the
// main bundle — but its screens don't. Neither the dialog portal nor the
// drawer renders children until it opens, so this chunk is fetched on the
// first download click rather than on page load (DEV-6485). The skeleton
// holds the surface open while that request is in flight.
const ConfirmDownloadModalScreens = dynamic(() => import('./screens'), {
  ssr: false,
  loading: () => <ConfirmDownloadScreensSkeleton />
});
import type { AssetGroup } from '@/types/asset';
import type { ButtonProps } from '@music-vine/cadence/ui';
import type { CSSProperties } from 'react';
interface ConfirmDownloadModalProps {
  asChild?: boolean;
  asset: AssetGroup;
  /**
   * Open the download flow on mount instead of waiting for a button click, so
   * a caller outside a download button can drive it (see GlobalDownloadModal,
   * DEV-6514). Fires once, as soon as the gateway decision is ready.
   */
  autoOpen?: boolean;
  buttonVariant?: ButtonProps['size'];
  children?: React.ReactNode;
  className?: string;
  hideLabelOnMobile?: boolean;
  isGeneratingLicense?: boolean;
  onClose?: (wasKeyboardClose?: boolean) => void;
  onCloseAutoFocus?: () => void;
  onDownloadClick?: () => void;
  onOpen?: () => void;
  premium: boolean;
  zIndex?: number;
}

// Subscription-type → analytics plan-tier label. Distinct from the gateway's
// tier classification: this is for event tagging only.
const planTypeForSubscription = (subscriptionType: ReturnType<typeof useSubscriptionType>): 'pro' | 'creator' | 'essential' | 'free' => {
  if (subscriptionType === 'Pro' || subscriptionType === 'Business') {
    return 'pro';
  }
  if (subscriptionType === 'Creator') {
    return 'creator';
  }
  if (subscriptionType === 'Essentials') {
    return 'essential';
  }
  return 'free';
};
const ConfirmDownloadModal = memo(({
  isGeneratingLicense,
  premium: assetIsPremium,
  asset,
  autoOpen,
  buttonVariant = 'icon',
  hideLabelOnMobile = true,
  className,
  zIndex,
  onCloseAutoFocus,
  onOpen,
  onClose,
  onDownloadClick,
  asChild,
  children
}: ConfirmDownloadModalProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [directDownloadAllowed, setDirectDownloadAllowed] = useState(false);
  const [showPaymentIssueDialog, setShowPaymentIssueDialog] = useState(false);
  const [directDownloadInProgress, setDirectDownloadInProgress] = useState(false);
  const wasDialogOpenRef = useRef(false);
  const closedByKeyboardRef = useRef(false);
  const openedByPointerRef = useRef(false);
  useEffect(() => {
    if (isDialogOpen && !wasDialogOpenRef.current) {
      onOpen?.();
    }
    if (!isDialogOpen && wasDialogOpenRef.current) {
      onClose?.(closedByKeyboardRef.current);
    }
    wasDialogOpenRef.current = isDialogOpen;
  }, [isDialogOpen, onOpen, onClose]);
  const {
    openModalWithConversionPoint
  } = useAccountModals();
  const {
    open: openStage
  } = useStage();
  const setPauseWarningAsset = useSetAtom(pauseDownloadWarningAssetAtom);
  const hostMenu = useContext(MenuContext);
  const {
    isMd,
    isSm,
    isXl
  } = useTWScreens();
  const {
    asPath
  } = useRouter();
  const {
    overduePayment
  } = useOverduePayment();

  // Upsell state lives behind the gateway so this modal isn't responsible
  // for the role projection, credit reconciliation, and mobile-animation
  // buffer that opening the upsell modal requires.
  const isUpsellModalOpen = useIsUpsellModalOpen();
  const triggerAssetUpsell = useAssetUpsellTrigger();
  const {
    decision
  } = useDownloadGateway(asset);

  // New download journey (DEV-6514): route anonymous sign-up/login through
  // the DialogStage flow that shows the asset being downloaded. Off by
  // default; falls back to the legacy account modals below.
  const newDownloadFlow = useFeatureIsOn('new-download-flow');
  const openDownloadAuth = useOpenDownloadAuth();

  // New download journey (DEV-6530): an authenticated user's *first* download
  // is routed through the "Where will you use this asset?" step regardless of
  // how they signed up. Once offered, subsequent downloads fall back to the
  // regular journey below.
  const shouldOfferFirstDownloadUsage = useShouldOfferFirstDownloadUsage();
  const openFirstDownloadUsage = useOpenFirstDownloadUsage();

  // DEV-7218: free users on a premium Music download are split between the
  // premium journey (control) and the compact upsell side modal (variant).
  const getFreePremiumDownloadArm = useFreePremiumDownloadArm();
  const subscriptionType = useSubscriptionType();
  const planType = useMemo(() => planTypeForSubscription(subscriptionType), [subscriptionType]);
  const {
    isDownloaded: assetHasBeenDownloaded
  } = useDownloads({
    id: Number(asset.data.id),
    type: asset.type
  });
  const isDownloaded = (asset.type === ASSET_TYPE.MUSIC || asset.type === ASSET_TYPE.SFX) && !!assetHasBeenDownloaded;

  // A/B test: free vs premium asset CTA.
  const isExplicitCtaExperiment = useExplicitCtaExperiment();
  const experimentButton = useExperimentButton(assetIsPremium, isExplicitCtaExperiment);
  let buttonSize = isMd ? buttonVariant : 'icon';
  let showButtonLabel = buttonVariant !== 'icon' && isMd;
  if (!hideLabelOnMobile) {
    showButtonLabel = true;
    buttonSize = buttonVariant;
  }
  const logDownloadIntent = useCallback(() => {
    logEvent({
      event_type: 'click',
      asPath,
      metadata: {
        type: 'download_intent',
        asset_type: asset.type,
        plan_type: planType,
        asset_id: Number(asset.data.id)
      }
    });
  }, [asPath, asset.data.id, asset.type, planType]);
  const handleModalOpen = useCallback(() => {
    if (isDialogOpen || isUpsellModalOpen) {
      return;
    }
    const freePremiumDownloadArm = getFreePremiumDownloadArm(asset, decision);

    // The control is the same premium-gate journey a first download gets.
    if (freePremiumDownloadArm === 'control') {
      logDownloadIntent();
      openFirstDownloadUsage(asset, decision);
      return;
    }

    // DEV-6530: first-download detour. Only for terminal decisions (a real
    // download / format choice / upsell) — anonymous sign-up and the
    // verify-email / overdue-payment gates still resolve through the switch.
    if (shouldOfferFirstDownloadUsage && !freePremiumDownloadArm && isFirstDownloadTerminalDecision(decision)) {
      logDownloadIntent();
      openFirstDownloadUsage(asset, decision);
      return;
    }
    switch (decision.action) {
      case 'pending':
        return;
      case 'navigate':
        {
          if (newDownloadFlow && (decision.to === 'sign-up' || decision.to === 'login')) {
            // The opener re-derives returning-user routing itself, so both
            // branches funnel through here.
            openDownloadAuth(asset);
            return;
          }
          if (decision.to === 'sign-up') {
            openModalWithConversionPoint(AccountModalNames.SignUpAllPlans, ModalConversionPoints.SignUp, {
              welcomeMessageType: decision.welcomeMessageType
            });
            return;
          }
          if (decision.to === 'login') {
            openModalWithConversionPoint(AccountModalNames.Login, ModalConversionPoints.ReturningUserLogin, {
              welcomeMessageType: decision.welcomeMessageType
            });
            return;
          }
          if (decision.to === 'verify-email') {
            openModalWithConversionPoint(AccountModalNames.VerifyEmail, ModalConversionPoints.SignUp);
            return;
          }
          if (decision.to === 'pause-warning') {
            const {
              assetName,
              assetThumbnail,
              contributorImage,
              isPremium
            } = createAssetInfo(asset);
            setPauseWarningAsset({
              assetName,
              imageUrl: assetThumbnail ?? contributorImage,
              isPremium,
              type: asset.type
            });
            // Close the hosting drawer first; on mobile wait out its ~300 ms
            // dismiss animation before opening the stage (mirrors the upsell
            // trigger's drawer-animation buffer).
            hostMenu?.setIsOpen(false);
            if (isSm) {
              openStage('cancellation.pause-download-warning');
            } else {
              setTimeout(() => {
                openStage('cancellation.pause-download-warning');
              }, 300);
            }
            return;
          }
          // overdue-payment. Guard on `overduePayment` to match the dialog's
          // own render condition below — both read the same `useOverduePayment`
          // hook, so this is always satisfied when the gateway routes here, but
          // keeping them symmetric prevents a silent dead-click if they ever
          // diverge (mirrors download-button-handler.tsx).
          if (overduePayment) {
            setShowPaymentIssueDialog(true);
          }
          return;
        }
      case 'upsell':
        {
          logDownloadIntent();
          triggerAssetUpsell(asset, {
            compact: freePremiumDownloadArm === 'variant',
            hasNoDownloadsLeft: decision.hasNoDownloadsLeft,
            reason: decision.reason,
            onBeforeOpen: () => {
              setIsDialogOpen(false);
              // The variant's side modal otherwise opens over the mobile drawer.
              if (freePremiumDownloadArm === 'variant') {
                hostMenu?.setIsOpen(false);
              }
            }
          });
          return;
        }
      case 'ask-format':
        {
          logDownloadIntent();
          setIsDialogOpen(true);
          return;
        }
      case 'download':
        {
          logDownloadIntent();
          setIsDialogOpen(true);
          if (!decision.requiresConfirmation) {
            setDirectDownloadAllowed(true);
            setDirectDownloadInProgress(true);
          }
          return;
        }
    }
  }, [isDialogOpen, isUpsellModalOpen, decision, asset, overduePayment, openModalWithConversionPoint, openStage, setPauseWarningAsset, hostMenu, isSm, triggerAssetUpsell, logDownloadIntent, newDownloadFlow, openDownloadAuth, shouldOfferFirstDownloadUsage, openFirstDownloadUsage, getFreePremiumDownloadArm]);
  const handleDownloadButtonClick = useCallback(() => {
    onDownloadClick?.();
    handleModalOpen();
  }, [onDownloadClick, handleModalOpen]);

  // Auto-open path (GlobalDownloadModal, DEV-6514): fire the same open logic a
  // click would, but only once and only after the gateway decision resolves
  // — opening on a `pending` decision would silently no-op and never retry.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpen && !autoOpenedRef.current && decision.action !== 'pending') {
      autoOpenedRef.current = true;
      handleModalOpen();
    }
  }, [autoOpen, decision.action, handleModalOpen]);
  const value = useMemo(() => ({
    isDialogOpen,
    setIsDialogOpen
  }), [isDialogOpen]);
  const content = <>
        {overduePayment && <PaymentIssueDownloadDialog asset={asset} drawer={!isSm} isPremiumAsset={assetIsPremium} onOpenChange={setShowPaymentIssueDialog} open={showPaymentIssueDialog} />}

        <ConfirmDownloadModalContext.Provider value={value}>
          {isSm ? <SteppedDialog.Root onOpenChange={setIsDialogOpen} open={isDialogOpen}>
              <SteppedDialog.Portal>
                <SteppedDialogContentContainer onCloseAutoFocus={e => {
            if (closedByKeyboardRef.current) {
              closedByKeyboardRef.current = false;
            } else {
              e.preventDefault();
            }
            onCloseAutoFocus?.();
          }} onEscapeKeyDown={() => {
            closedByKeyboardRef.current = true;
          }} onOpenAutoFocus={e => {
            if (openedByPointerRef.current) {
              e.preventDefault();
              openedByPointerRef.current = false;
            }
          }} style={{
            '--stepped-dialog-z-index': zIndex ?? 20
          } as CSSProperties}>
                  <SteppedDialog.ScreenContainer>
                    <ConfirmDownloadModalScreens asset={asset} isDirectDownload={directDownloadAllowed} onDirectDownloadCompleted={() => setDirectDownloadInProgress(false)} overduePayment={overduePayment} />
                  </SteppedDialog.ScreenContainer>
                </SteppedDialogContentContainer>
              </SteppedDialog.Portal>
            </SteppedDialog.Root> : <SteppedDrawer.Root onOpenChange={setIsDialogOpen} open={isDialogOpen}>
              <SteppedDrawer.Content className="z-[9999]" onCloseAutoFocus={e => {
          if (closedByKeyboardRef.current) {
            closedByKeyboardRef.current = false;
          } else {
            e.preventDefault();
          }
          onCloseAutoFocus?.();
        }} onOpenAutoFocus={e => {
          if (openedByPointerRef.current) {
            e.preventDefault();
            openedByPointerRef.current = false;
          }
        }}>
                <SteppedDrawer.ScreenContainer>
                  <ConfirmDownloadModalScreens asset={asset} isDirectDownload={directDownloadAllowed} isDrawer onDirectDownloadCompleted={() => setDirectDownloadInProgress(false)} overduePayment={overduePayment} />
                </SteppedDrawer.ScreenContainer>
              </SteppedDrawer.Content>
            </SteppedDrawer.Root>}
        </ConfirmDownloadModalContext.Provider>
      </>;
  const Comp = asChild ? Slot.Root : Button;
  const isRoundIconButton = !(isExplicitCtaExperiment || showButtonLabel && (isXl || !hideLabelOnMobile));
  const ExperimentIcon = experimentButton.icon;

  // Never overrides the experiment's Subscribe CTA (DEV-5247)
  const buttonText = isDownloaded && experimentButton.text === 'Download' ? 'Download again' : experimentButton.text;
  const props = asChild ? {
    onClick: handleDownloadButtonClick,
    disabled: isGeneratingLicense ?? directDownloadInProgress
  } : {
    variant: experimentButton.variant,
    size: (() => {
      if (showButtonLabel && isXl) {
        return 'lg';
      }
      if (showButtonLabel && isSm) {
        return 'default';
      }
      if (isExplicitCtaExperiment) {
        return 'default';
      }
      return buttonSize || 'default';
    })(),
    icon: isExplicitCtaExperiment ? false : !isXl && hideLabelOnMobile,
    className: cn({
      'rounded-full': isRoundIconButton,
      'group relative': isDownloaded && isRoundIconButton,
      '@[700px]:rounded-md rounded-full': isExplicitCtaExperiment && hideLabelOnMobile,
      '@[700px]:aspect-[unset] aspect-square @[700px]:min-w-[117px] p-0 @[700px]:px-3': isExplicitCtaExperiment && hideLabelOnMobile,
      'min-w-[117px] rounded-md px-3': isExplicitCtaExperiment && !hideLabelOnMobile,
      'max-sm:h-10 max-sm:w-full max-sm:text-lg': showButtonLabel && isXl,
      'text-sm': isExplicitCtaExperiment && buttonSize !== 'default'
    }, className),
    onClick: handleDownloadButtonClick,
    disabled: isGeneratingLicense ?? directDownloadInProgress
  };
  const hasButtonLabel = showButtonLabel && (isXl || !hideLabelOnMobile);
  const buttonContent = <>
        <ExperimentIcon className="size-4" />
        <span className={cn(isExplicitCtaExperiment ? hideLabelOnMobile ? 'sr-only @[700px]:not-sr-only' : '' : !hasButtonLabel && 'sr-only')}>
          {buttonText}
        </span>
      </>;
  return <>
        <Tooltip className={cn(buttonVariant !== 'icon' && 'hidden', isExplicitCtaExperiment && 'hidden')} delayDuration={250} label={buttonText}>
          <Comp {...props} data-testid="download-button" onPointerDown={() => {
        openedByPointerRef.current = true;
      }}>
            {asChild ? children : <>
                {children ?? buttonContent}
                {!!isDownloaded && isRoundIconButton && <DownloadedRing className="pointer-events-none absolute top-1/2 left-1/2 size-[calc(100%+6.66px)] -translate-x-1/2 -translate-y-1/2 text-brand-primary group-hover:text-brand-primary-hover" />}
              </>}
          </Comp>
        </Tooltip>
        {content}
      </>;
}, (prevProps, nextProps) => prevProps.isGeneratingLicense === nextProps.isGeneratingLicense && prevProps.premium === nextProps.premium && prevProps.buttonVariant === nextProps.buttonVariant && prevProps.hideLabelOnMobile === nextProps.hideLabelOnMobile && prevProps.className === nextProps.className && prevProps.zIndex === nextProps.zIndex && prevProps.asChild === nextProps.asChild && prevProps.onDownloadClick === nextProps.onDownloadClick && prevProps.children === nextProps.children && prevProps.asset?.type === nextProps.asset?.type && prevProps.asset?.data?.id === nextProps.asset?.data?.id && prevProps.asset?.data?.name === nextProps.asset?.data?.name && prevProps.asset?.data?.is_premium === nextProps.asset?.data?.is_premium && (prevProps.asset?.type === 'motion' ? prevProps.asset?.data?.is_new === nextProps.asset?.data?.is_new : true));
export default ConfirmDownloadModal;
ConfirmDownloadModal.displayName = 'ConfirmDownloadModal';